/**
 * LandingEditor — split-screen editor orchestrating the Fork 5 flow.
 *
 *   LEFT: current landing iframe + draft-body iframe
 *   RIGHT: VariationPanel (generate variations)
 *          TemplateLibrary (apply saved templates)
 *          Deploy + Rollback buttons
 *
 * Fetches the current Event.PageDescription on mount via /api/landing/:id
 * and maintains a draft body the user can deploy or save as a template.
 */

import { useCallback, useEffect, useState } from 'react'
import { FileImage, RotateCcw, Save, UploadCloud } from 'lucide-react'
import { SandboxedPreview } from './SandboxedPreview'
import { VariationPanel, type Variation } from './VariationPanel'
import { TemplateLibrary, type LandingTemplate } from './TemplateLibrary'
import { DeployModal } from './DeployModal'

interface LandingEditorProps {
  eventId: number
  eventShortname: string
  eventStartIso?: string | null
  /** fetch-compatible client so tests can inject a mock. */
  http?: typeof fetch
}

/**
 * Stable module-level default so React's referential equality on the `http`
 * prop doesn't thrash every render. Previously `http = (...args) => fetch(...args)`
 * was declared as a default in the function signature, which creates a fresh
 * function identity on every parent render. That new identity invalidated the
 * `useCallback(jsonFetch, [http])` memo, which rebuilt on every render, which
 * invalidated `useEffect(..., [eventId, jsonFetch])`, which re-fired the
 * /api/landing/:id fetch. Each fetch's `.then` called `setTemplates(newArray)`
 * with a fresh array reference — triggering another render — and the cycle
 * never terminated. Result: "Loading event 188…" forever + an infinite
 * stream of /api/landing/188 + /api/landing/templates hits to the backend
 * (reproduced 2026-04-24 with Puppeteer against sean.magicmeade.cloud).
 *
 * Binding `fetch` directly as the default fixes it — `fetch` is a stable
 * global reference. The arrow wrapper used to exist to satisfy older TS
 * ergonomics but is no longer needed.
 */
const DEFAULT_HTTP: typeof fetch = (...args) => fetch(...args)

export function LandingEditor({
  eventId,
  eventShortname,
  eventStartIso,
  http = DEFAULT_HTTP,
}: LandingEditorProps) {
  const [currentHtml, setCurrentHtml] = useState<string>('')
  const [draftHtml, setDraftHtml] = useState<string>('')
  const [templates, setTemplates] = useState<LandingTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deployOpen, setDeployOpen] = useState(false)
  const [lastResult, setLastResult] = useState<{
    status?: string
    snapshotId?: string
    response_classification?: string
  } | null>(null)

  const jsonFetch = useCallback(
    async (input: string, init?: RequestInit) => {
      const res = await http(input, {
        ...init,
        credentials: 'same-origin',
        headers: {
          ...(init?.headers || {}),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      const text = await res.text()
      let body: unknown = null
      try {
        body = text ? JSON.parse(text) : null
      } catch {
        body = text
      }
      if (!res.ok) {
        const message = (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string')
          ? (body as { message: string }).message
          : `HTTP ${res.status}`
        throw new Error(message)
      }
      return body
    },
    [http],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    // Split the two reads so a 503 on /api/landing/:eventId (landing-read
    // not wired at this server.js mount) does not hard-fail the whole page.
    // Templates still render + user can preview/apply/save; the
    // "current body" pane just shows empty. Codex F13 re-review HIGH #1b.
    Promise.allSettled([
      jsonFetch(`/api/landing/${eventId}`),
      jsonFetch('/api/landing/templates'),
    ])
      .then(([landingResult, libraryResult]) => {
        if (cancelled) return
        if (landingResult.status === 'fulfilled') {
          const html =
            (landingResult.value as { current?: { html?: string } })?.current?.html || ''
          setCurrentHtml(html)
          setDraftHtml(html)
        } else {
          // Landing-read unavailable (503 not_wired or any other failure).
          // Start the editor with an empty current body. Deploy path still
          // requires the server-side write hook; if it's also unwired, the
          // deploy handler returns 503 landing_write_not_wired and the
          // DeployModal shows that error.
          setCurrentHtml('')
          setDraftHtml('')
        }
        if (libraryResult.status === 'fulfilled') {
          const list =
            (libraryResult.value as { templates?: LandingTemplate[] })?.templates || []
          setTemplates(list)
        } else {
          // Templates being unreachable is the more serious failure — at
          // least tell the user.
          setError((libraryResult.reason as Error).message)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [eventId, jsonFetch])

  const generate = useCallback(
    async ({ userIntent, count }: { userIntent: string; count: number }) => {
      const body = await jsonFetch(`/api/landing/${eventId}/variations`, {
        method: 'POST',
        body: JSON.stringify({ currentHtml: draftHtml, userIntent, count }),
      })
      return body as { variations: Variation[]; errors?: string[] }
    },
    [eventId, draftHtml, jsonFetch],
  )

  const saveAsTemplate = useCallback(async () => {
    const name = window.prompt('Template name', `${eventShortname} custom`)
    if (!name) return
    const res = (await jsonFetch('/api/landing/templates', {
      method: 'POST',
      body: JSON.stringify({
        name: name.trim(),
        description: `Snapshot from event ${eventShortname}`,
        htmlBody: draftHtml,
        tags: ['custom', eventShortname],
      }),
    })) as { template: LandingTemplate }
    setTemplates((prev) => [res.template, ...prev])
  }, [draftHtml, eventShortname, jsonFetch])

  const applyTemplate = useCallback(
    async (tpl: LandingTemplate) => {
      const res = (await jsonFetch(`/api/landing/${eventId}/from-template/${tpl.id}`, {
        method: 'POST',
        body: JSON.stringify({
          values: {
            event_name: eventShortname,
          },
        }),
      })) as { html: string }
      setDraftHtml(res.html)
    },
    [eventId, eventShortname, jsonFetch],
  )

  const deleteTemplate = useCallback(
    async (tpl: LandingTemplate) => {
      await jsonFetch(`/api/landing/templates/${tpl.id}`, { method: 'DELETE' })
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id))
    },
    [jsonFetch],
  )

  const renameTemplate = useCallback(
    async (tpl: LandingTemplate, name: string) => {
      const res = (await jsonFetch(`/api/landing/templates/${tpl.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
      })) as { template: LandingTemplate }
      setTemplates((prev) => prev.map((t) => (t.id === tpl.id ? res.template : t)))
    },
    [jsonFetch],
  )

  const deploy = useCallback(
    async ({ dryRun, typedConfirmation, operatorComment }: {
      dryRun: boolean
      typedConfirmation: string
      eventShortname: string
      operatorComment: string
    }) => {
      const res = (await jsonFetch(`/api/landing/${eventId}/deploy`, {
        method: 'POST',
        body: JSON.stringify({
          html: draftHtml,
          dryRun,
          eventShortname,
          eventStartIso,
          typedConfirmation,
          operatorComment,
        }),
      })) as {
        snapshotId: string
        dryRun: boolean
        writeResult?: { status?: string; response_classification?: string }
      }
      setLastResult({
        snapshotId: res.snapshotId,
        status: res.writeResult?.status,
        response_classification: res.writeResult?.response_classification,
      })
      setDeployOpen(false)
    },
    [draftHtml, eventId, eventShortname, eventStartIso, jsonFetch],
  )

  const rollback = useCallback(async () => {
    if (!window.confirm('Roll back to the most recent snapshot?')) return
    const res = (await jsonFetch(`/api/landing/${eventId}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ dryRun: true }),
    })) as { restoredFromSnapshotId?: string }
    window.alert(`Rollback dry-run queued (snapshot ${res.restoredFromSnapshotId ?? 'n/a'}). Un-check dry-run in the deploy modal to apply live.`)
  }, [eventId, jsonFetch])

  if (loading) {
    return <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">Loading event {eventId}…</div>
  }

  if (error) {
    return (
      <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        Could not load landing: {error}
      </div>
    )
  }

  return (
    <div data-testid="landing-editor" className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current on butler1</h2>
          <button
            type="button"
            onClick={rollback}
            className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
          >
            <RotateCcw className="h-3 w-3" />
            Rollback
          </button>
        </div>
        <SandboxedPreview html={currentHtml || '<html><body style="display:flex;align-items:center;justify-content:center;height:100%;font-family:sans-serif;color:#8697A8">No landing on butler1 yet</body></html>'} title="Current landing" height={360} />
        <div className="mt-4 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-butler-600">Draft preview</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={saveAsTemplate}
              className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
            >
              <Save className="h-3 w-3" />
              Save as template
            </button>
            <button
              type="button"
              onClick={() => setDeployOpen(true)}
              className="flex items-center gap-1 rounded-md bg-marquee-500 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white hover:bg-marquee-600"
              data-testid="open-deploy-modal"
            >
              <UploadCloud className="h-3 w-3" />
              Deploy…
            </button>
          </div>
        </div>
        <SandboxedPreview html={draftHtml || '<html><body>Draft empty — apply a template or generate a variation.</body></html>'} title="Draft landing" height={360} />
        {lastResult && (
          <div className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-600 dark:bg-slate-900 dark:text-slate-300">
            Last result: <span className="font-mono">{lastResult.response_classification || lastResult.status}</span>
            {lastResult.snapshotId && (
              <> · snapshot <span className="font-mono">{lastResult.snapshotId}</span></>
            )}
          </div>
        )}
      </section>
      <section className="flex flex-col gap-6">
        <div>
          <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <FileImage className="h-3 w-3" />
            Template library
          </h2>
          <TemplateLibrary
            templates={templates}
            onApply={applyTemplate}
            onRename={renameTemplate}
            onDelete={deleteTemplate}
          />
        </div>
        <div>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">AI variations</h2>
          <VariationPanel
            currentHtml={draftHtml}
            onGenerate={generate}
            onAccept={(html) => setDraftHtml(html)}
          />
        </div>
      </section>
      <DeployModal
        open={deployOpen}
        onClose={() => setDeployOpen(false)}
        onDeploy={deploy}
        currentHtml={currentHtml}
        nextHtml={draftHtml}
        eventShortname={eventShortname}
        eventStartIso={eventStartIso}
        rollbackSnapshotId={lastResult?.snapshotId || null}
      />
    </div>
  )
}
